import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

type Language = 'en' | 'sv';

interface LanguageContextType {
  language: Language;
  setLanguage(language: Language): void;
  t(key: string): string;
}

const translations = {
  en: {
    // Header
    'nav.dt': 'Digital Transformation',
    'nav.management': 'Management',
    'nav.security': 'Security',
    'nav.software': 'Software Development',
    'nav.about': 'About',
    'nav.news': 'Conference',
    'nav.getStarted': 'Get Started',
    'nav.menu': 'Menu',

    // Hero
    'hero.title1': 'Strategy, People & Technology',
    'hero.title2': 'for the Digital Era',
    'hero.description':
      'From digital transformation strategy and change leadership to security and hands-on software development — we help organizations turn technology into real, lasting value.',
    'hero.aiPromo': "New: Build a custom AI chatbot trained on your company's information.",
    'hero.aiLearnMore': 'Learn more',
    'hero.startProject': 'Contact Us',
    'hero.viewWork': 'View Our Work',
    'hero.stats.projects': 'Projects Delivered',
    'hero.stats.experience': 'Years Experience',
    'hero.stats.satisfaction': 'Client Satisfaction',
    'hero.stats.support': 'Support Available',

    // Services
    'services.title': 'Our Services',
    'services.description':
      'We deliver comprehensive software solutions that drive business growth and innovation.',
    'services.ai.title': 'AI Integration',
    'services.ai.description':
      'Intelligent solutions that automate processes and enhance decision-making with cutting-edge artificial intelligence.',
    'services.fullstack.title': 'Fullstack Development',
    'services.fullstack.description':
      'End-to-end web and mobile application development using modern frameworks and best practices.',
    'services.cloud.title': 'Cloud Engineering',
    'services.cloud.description':
      'Scalable cloud infrastructure design and deployment on AWS, Azure, and Google Cloud platforms.',
    'services.cicd.title': 'CI/CD Implementation',
    'services.cicd.description':
      'Automated deployment pipelines that ensure fast, reliable, and consistent software delivery.',
    'services.data.title': 'Data Solutions',
    'services.data.description':
      'Database design, optimization, and data pipeline creation for efficient information management.',
    'services.security.title': 'Security Consulting',
    'services.security.description':
      'Comprehensive security audits and implementation of robust cybersecurity measures.',

    // AI Section
    'aiSection.badge': 'AI for your data',
    'aiSection.title': 'Custom AI Chatbots for Your Company',
    'aiSection.description':
      "We build secure AI assistants trained on your company's documents, knowledge bases, and processes. Deploy on your website, Slack/Teams, or inside your product.",
    'aiSection.point1': 'Trained on your PDFs, docs, wikis, and ticketing data',
    'aiSection.point2': 'Accurate, source-cited answers and document retrieval (RAG)',
    'aiSection.point3': 'Secure by design: your data stays private',
    'aiSection.point4': 'Multi-channel: website widget, Slack/Teams, internal portal',
    'aiSection.point5': 'Compliance ready: GDPR-aware, role-based access',
    'aiSection.cta': 'Discuss your AI assistant',

    // Pillars (home)
    'pillars.title': 'What We Do',
    'pillars.description':
      'Four areas of expertise that together take your organization from idea to lasting change.',
    'pillars.dt.title': 'Digital Transformation',
    'pillars.dt.description':
      'Change management and strategy that connect digitalization to business goals — so new technology actually creates value.',
    'pillars.management.title': 'Management & Leadership',
    'pillars.management.description':
      'Develop the leadership capabilities that determine whether the digital journey succeeds — from strategic thinking to data-driven decisions.',
    'pillars.security.title': 'Security & Privacy',
    'pillars.security.description':
      'Practical tools and guidance to protect accounts and personal data — breach checks, GDPR erasure requests and more.',
    'pillars.software.title': 'Software Development',
    'pillars.software.description':
      'Fullstack development, AI integrations and cloud engineering — scalable software tailored to your business.',
    'pillars.explore': 'Learn more',

    // Digital Transformation page
    'dt.hero.badge': 'Offering 2026',
    'dt.hero.titlePre': 'Change management and',
    'dt.hero.titleAccent': 'leadership',
    'dt.hero.titlePost': 'in digital transformation.',
    'dt.hero.description':
      'Digital transformation is not just about introducing new technology. Above all, it is about changing ways of working, culture, competencies and behaviours — to create value for the organization and its citizens, customers or users.',
    'dt.hero.ctaContact': 'Book a meeting',
    'dt.hero.ctaManagement': 'Five key leadership capabilities',
    'dt.hero.quote': '“People don’t follow a technology — they follow a meaningful vision.”',
    'dt.hero.imageAlt': 'Leadership in digital transformation',
    'dt.change.badge': 'Change Management',
    'dt.change.title': 'New solutions only create value when people actually use them.',
    'dt.change.description':
      'Change management ensures that people understand, accept and use new solutions and ways of working. It is the bridge between the technology investment and its real effect in the organization.',
    'dt.leadership.badge': 'Leadership',
    'dt.leadership.title': 'From control to direction, engagement and learning.',
    'dt.leadership.description':
      'Digital leadership today is less about control and more about creating direction, engagement and learning. The leader is the one who holds technology, people and business together over time.',
    'dt.leadership.link': 'Explore management & leadership',
    'dt.challenges.badge': 'Common Challenges',
    'dt.challenges.title': 'Where leadership makes the difference.',
    'dt.challenges.description':
      'The same patterns recur in organization after organization. This is what the role of leadership looks like in practice.',
    'dt.challenges.1.challenge': 'Resistance to change',
    'dt.challenges.1.role': 'Create understanding and participation',
    'dt.challenges.2.challenge': 'Unclear goals and vision',
    'dt.challenges.2.role': 'Communicate direction and value',
    'dt.challenges.3.challenge': 'Lack of digital competence',
    'dt.challenges.3.role': 'Ensure learning and support',
    'dt.challenges.4.challenge': 'Silo thinking',
    'dt.challenges.4.role': 'Foster collaboration across boundaries',
    'dt.challenges.5.challenge': 'Change fatigue',
    'dt.challenges.5.role': 'Prioritize, celebrate progress, create energy',
    'dt.cta.badge': 'Let’s Talk',
    'dt.cta.title': 'Equip your leaders for the next step in the digital transformation.',
    'dt.cta.description':
      'We work with management teams, managers and change leaders in the public sector and private industry — with programs, coaching and strategic support.',
    'dt.cta.button': 'Book a meeting',

    // Management page
    'management.hero.badge': 'Management & Leadership',
    'management.hero.title': 'Leadership that holds technology, people and business together.',
    'management.hero.description':
      'Digital transformation happens in complex environments where not every answer exists from the start. Modern leadership is about creating direction, engagement and learning — and holding technology, people and business together over time.',
    'management.comp.badge': 'Key Leadership Capabilities',
    'management.comp.title':
      'Five capabilities that determine whether the digital journey succeeds.',
    'management.comp.1.title': 'Strategic thinking',
    'management.comp.1.lead': 'Connect digitalization to business goals and societal value.',
    'management.comp.1.body':
      'Leaders need to translate technology into business and societal value — and keep asking: how does digitalization contribute to better results for the organization?',
    'management.comp.2.title': 'Leading through uncertainty',
    'management.comp.2.lead': 'Dare to experiment, manage risk, foster innovation.',
    'management.comp.2.body':
      'Digital transformation happens in complex environments where not every answer exists from the start. Courageous leaders create room for learning and experimentation.',
    'management.comp.3.title': 'Communicative leadership',
    'management.comp.3.lead': 'People don’t follow a technology — they follow a meaningful vision.',
    'management.comp.3.body':
      'Explain why the change matters, build engagement, and meet worry and resistance with clarity and presence.',
    'management.comp.4.title': 'Trust-based leadership',
    'management.comp.4.lead': 'From command and control to trust and collaboration.',
    'management.comp.4.body':
      'Digital development requires cross-functional collaboration and decentralized decision-making — built on accountability and trust.',
    'management.comp.5.title': 'Data-driven leadership',
    'management.comp.5.lead': 'Decisions based on data, analysis and follow-up.',
    'management.comp.5.body':
      'Leaders need to interpret data, ask the right questions and turn insights into business development.',
    'management.reflection.badge': 'Reflection Question',
    'management.reflection.quote':
      '“How does digitalization contribute to better results for your organization?”',
    'management.reflection.note':
      'A question every management team should be able to answer — concretely, measurably and meaningfully.',
    'management.dtLink': 'Read about digital transformation',

    // Software Development page
    'software.hero.badge': 'Software Development',
    'software.hero.title1': 'Expert Software Solutions',
    'software.hero.title2': 'for Modern Business',
    'software.hero.description':
      'Fullstack development, AI integrations, and cloud engineering solutions tailored for your unique business needs. Transform your ideas into scalable software.',

    // Expertise
    'expertise.title': 'Technology Expertise',
    'expertise.description':
      'We work with cutting-edge technologies to deliver robust, scalable solutions.',
    'expertise.more': 'And many more technologies based on your project needs',

    // About
    'about.title': 'Why Choose Cernea Consulting?',
    'about.description':
      'We are a specialized software development company with deep expertise in fullstack development, AI integrations, and cloud engineering. Our mission is to transform your business ideas into powerful, scalable software solutions.',
    'about.benefit1': 'Agile development methodology',
    'about.benefit2': 'Transparent communication',
    'about.benefit3': 'Scalable architecture design',
    'about.benefit4': 'Post-launch support & maintenance',
    'about.benefit5': 'Competitive pricing',
    'about.benefit6': 'Quick turnaround times',
    'about.discuss': "Let's Discuss Your Project",
    'about.ready.title': 'Ready to Get Started?',
    'about.ready.description':
      "Contact us today for a free consultation and let's bring your vision to life.",
    'about.email': 'Email',
    'about.responseTime': 'Response Time',
    'about.responseValue': 'Within 24 hours',
    'about.consultation': 'Consultation',
    'about.consultationValue': 'Free initial assessment',

    // Contact Modal
    'contact.title': 'Contact Us',
    'contact.description':
      "Tell us how we can assist or help out with your project and we'll get back to you within 24 hours.",
    'contact.email': 'Email',
    'contact.emailPlaceholder': 'your.email@company.com',
    'contact.message': 'Project Details',
    'contact.messagePlaceholder':
      'Tell us how we can help out or with your project, timeline, and requirements...',
    'contact.cancel': 'Cancel',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.error': 'Error',
    'contact.fillFields': 'Please fill in all fields',
    'contact.success': 'Message Sent!',
    'contact.successMessage': 'Thank you for your interest. Victor will get back to you soon!',
    'contact.errorMessage': 'Failed to send message. Please try again.',

    // Customers
    'customers.title': 'Companies and Partners',
    'customers.description':
      'We have the privilege of working with innovative companies across various industries.',

    // Footer
    'footer.description':
      'Expert software consulting, AI integration, and cloud engineering solutions tailored for your business needs.',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.location': 'Sweden',
    'footer.global': 'Available for global projects',
    'footer.copyright': '2025 Cernea Consulting AB. All rights reserved.',

    // News Dialog
    'news.title': 'Jönköping Unleashed',
    'news.description':
      "A conference for those building tomorrow's tech – with responsibility, insight and curiosity",
    'news.whatToExpect': 'What to expect:',
    'news.aiRegulations': 'AI regulations and development of responsible AI',
    'news.webTech': 'Powerful web technology as a distribution platform',
    'news.accessibility': 'Accessibility in web development',
    'news.agile': 'Agile development in large organizations',
    'news.networking': 'Networking and mingling with tech professionals',
    'news.freeEvent': 'Free event!',
    'news.eventDescription':
      'A tech conference focused on current processes, laws, techniques and working methods in the constantly changing tech world.',
    'news.getTickets': 'Get Tickets',
    'news.maybeLater': 'Maybe Later',
    'news.imageAlt': 'Jönköping Unleashed Conference - Click to visit event page',

    // Conference page
    'conference.techConference': 'Tech conference',
    'conference.location': 'Jönköping, Sweden',
    'conference.pretitle': "Build what's next — together",
    'conference.formatLabel': 'Format',
    'conference.locationLabel': 'Location',
    'conference.vibeLabel': 'Vibe',
    'conference.aiShort': 'AI',

    // Security page
    'security.title': 'Online Security & Privacy',
    'security.intro':
      'Practical tips to protect your privacy online and quickly check if a password has appeared in data breaches.',
    'security.tips.title1': 'Use a password manager',
    'security.tips.desc1':
      'Generate unique, long passwords for every site and store them in a reputable manager.',
    'security.tips.title2': 'Enable two-factor authentication (2FA)',
    'security.tips.desc2':
      'Add an extra step when logging in to keep accounts safe even if a password leaks.',
    'security.tips.title3': 'Beware of phishing',
    'security.tips.desc3':
      'Double-check sender addresses and URLs; never share passwords over email or chat.',
    'security.checker.badge': 'Password health',
    'security.checker.title': 'Check if your password has been pwned',
    'security.checker.subtitle':
      'Your password is hashed locally with SHA-1 and only the first 5 characters of the hash are sent to Have I Been Pwned (k-anonymity).',
    'security.checker.label': 'Password to check',
    'security.checker.placeholder': 'Enter a password to test (do not reuse this password)',
    'security.checker.show': 'Show',
    'security.checker.hide': 'Hide',
    'security.checker.checkButton': 'Check password',
    'security.checker.loading': 'Checking…',
    'security.checker.result.safe': 'Good news — this password was not found in known breaches.',
    'security.checker.result.pwned':
      'Warning — this password appears {count} times in known breaches. Change it everywhere it’s used.',
    'security.checker.result.error': 'Something went wrong. Please try again later.',
    'security.checker.errorEmpty': 'Please enter a password.',
    'security.checker.privacyNote':
      'We never send your actual password. Only a 5-character prefix of its SHA-1 hash is shared with the HIBP API.',
    'security.email.badge': 'Email breaches',
    'security.email.title': 'Check if your email appears in breaches',
    'security.email.subtitle':
      'Use Have I Been Pwned to see if your email address has been exposed in known data breaches.',
    'security.email.description':
      'This check happens on the Have I Been Pwned website. Never reuse passwords and enable 2FA where available.',
    'security.email.cta': 'Check your email on HIBP',
    'security.notify.badge': 'Email notifications',
    'security.notify.title': 'Get notified if your email is compromised',
    'security.notify.subtitle':
      'Have I Been Pwned can notify you if your email address appears in a new breach.',
    'security.notify.description':
      'Sign up with your email to receive alerts. Use strong, unique passwords and enable 2FA to reduce risk.',
    'security.notify.cta': 'Sign up for notifications on HIBP',
    'security.top.contactPrompt': 'Have questions or need help?',
    'security.top.contactLink': 'Contact us',

    // GDPR Erasure
    'security.gdpr.badge': 'GDPR & data erasure',
    'security.gdpr.title': 'Erase your public info from data brokers',
    'security.gdpr.subtitle':
      'Under GDPR (Art. 17) you can request removal of personal data. Select providers and send a request to erase your information. ',
    'security.gdpr.searchLabel': 'Search providers',
    'security.gdpr.searchPlaceholder': 'Search providers by name, country, or category…',
    'security.gdpr.selectAll': 'Select all',
    'security.gdpr.clear': 'Clear',
    'security.gdpr.selectedCount': '{count} selected',
    'security.gdpr.cta': 'Contact selected providers',
    'security.gdpr.disclaimer':
      'This tool helps draft emails to provider privacy teams. Requests are sent on your behalf with your email as Reply-To so they can respond directly.',
    'security.gdpr.googleRemoval.title': 'Request removal from Google Search',
    'security.gdpr.googleRemoval.description':
      'If sensitive or outdated results about you appear in Google Search, you can ask Google to remove personal content and keep similar searches filtered in the future.',
    'security.gdpr.googleRemoval.cta': 'Open Google removal form',

    // GDPR Modal
    'security.gdpr.modal.title': 'Send GDPR erasure request',
    'security.gdpr.modal.description':
      'Provide enough detail to verify your identity and help locate your records.',
    'security.gdpr.modal.providerCount': 'You will contact {count} provider(s).',
    'security.gdpr.modal.email': 'Your email',
    'security.gdpr.modal.fullName': 'Full name',
    'security.gdpr.modal.personalNr': 'Personal identity number (personnummer)',
    'security.gdpr.modal.personalNrPlaceholder': 'YYYYMMDD-XXXX',
    'security.gdpr.modal.identifiers': 'Identifiers (optional)',
    'security.gdpr.modal.identifiersPlaceholder': 'Other emails, aliases, phone number…',
    'security.gdpr.modal.country': 'Country/Region (optional)',
    'security.gdpr.modal.links': 'Profile links / URLs (optional)',
    'security.gdpr.modal.linksPlaceholder':
      'One per line, e.g. profile pages or search result URLs',
    'security.gdpr.modal.message': 'Additional context (optional)',
    'security.gdpr.modal.messagePlaceholder':
      'Hej!\n\n\nMitt personnummer är:\n\nJag vill härmed radera mina uppgifter om mig.\n\nJag begär att alla personuppgifter som är kopplade till mig tas bort från era databaser och webbplatser. Detta innebär att ingen person ska kunna se information om mig, inklusive rättsliga dokument och andra uppgifter kopplade till mitt namn och personnummer, i er tjänst. Jag vill även att ni, så långt det är möjligt, uppgiftsminimerar och raderar de uppgifter ni håller om mig internt.\n\nNi kan hitta information om era skyldigheter på Integritetsskyddsmyndighetens webbplats (https://www.imy.se).\n\nJag ser fram emot bekräftelse på min begäran om radering snarast möjligt. Ni får gärna kontakta mig om ni har några frågor.\n\n\nMed vänliga hälsningar, ',
    'security.gdpr.modal.help':
      'Include your full name, your email(s), and your Swedish personal identity number (personnummer). You may add other identifiers if helpful.',
    'security.gdpr.modal.previewHeading': 'Email preview',
    'security.gdpr.modal.cancel': 'Cancel',
    'security.gdpr.modal.send': 'Open mail client',
    'security.gdpr.modal.sending': 'Sending…',
    'security.gdpr.toast.successTitle': 'Requests sent',
    'security.gdpr.toast.partialTitle': 'Some requests failed',
    'security.gdpr.toast.summary': 'Requests sent: {success} succeeded, {failed} failed',
    'security.gdpr.toast.missingConfig': 'Mail configuration missing. Please contact support.',

    // GDPR Email content (prefilled email)
    'security.gdpr.email.subject': 'Request for erasure of personal data (GDPR)',
    'security.gdpr.email.greetingGeneric': 'Hello privacy team,',
    'security.gdpr.email.greetingProvider': 'Hello {provider} privacy team,',
    'security.gdpr.email.requestLine':
      'I am requesting erasure of my personal data under GDPR Article 17.',
    'security.gdpr.email.field.fullName': 'Full name',
    'security.gdpr.email.field.replyTo': 'Email (reply-to)',
    'security.gdpr.email.field.identifiers': 'Identifiers',
    'security.gdpr.email.field.country': 'Country/Region',
    'security.gdpr.email.linksHeading': 'Profile links / URLs:',
    'security.gdpr.email.closingConfirm':
      'Please confirm receipt and inform me when the erasure is completed.',
    'security.gdpr.email.thanks': 'Thank you.',

    // Common
    'common.home': 'Home',
    'common.back': 'Back',
  },
  sv: {
    // Header
    'nav.dt': 'Digital Transformation',
    'nav.management': 'Ledarskap',
    'nav.security': 'Säkerhet',
    'nav.software': 'Mjukvaruutveckling',
    'nav.about': 'Om Oss',
    'nav.news': 'Konferens',
    'nav.getStarted': 'Kom Igång',
    'nav.menu': 'Meny',

    // Hero
    'hero.title1': 'Strategi, Människor & Teknik',
    'hero.title2': 'för den Digitala Eran',
    'hero.description':
      'Från strategi för digital transformation och förändringsledarskap till säkerhet och praktisk mjukvaruutveckling — vi hjälper organisationer att omvandla teknik till verkligt och varaktigt värde.',
    'hero.aiPromo': 'Nytt: Bygg en anpassad AI‑chatbot tränad på ert företags information.',
    'hero.aiLearnMore': 'Läs mer',
    'hero.startProject': 'Kontakta Oss',
    'hero.viewWork': 'Se Vårt Arbete',
    'hero.stats.projects': 'Levererade Projekt',
    'hero.stats.experience': 'Års Erfarenhet',
    'hero.stats.satisfaction': 'Kundnöjdhet',
    'hero.stats.support': 'Support Tillgänglig',

    // Services
    'services.title': 'Våra Tjänster',
    'services.description':
      'Vi levererar omfattande mjukvarulösningar som driver affärstillväxt och innovation.',
    'services.ai.title': 'AI-integration',
    'services.ai.description':
      'Intelligenta lösningar som automatiserar processer och förbättrar beslutsfattande med banbrytande artificiell intelligens.',
    'services.fullstack.title': 'Fullstack-utveckling',
    'services.fullstack.description':
      'End-to-end webb- och mobilapplikations -utveckling med moderna ramverk och bästa praxis.',
    'services.cloud.title': 'Molnteknik',
    'services.cloud.description':
      'Skalbar molninfrastruktur design och distribution på AWS, Azure och Google Cloud-plattformar.',
    'services.cicd.title': 'CI/CD-implementation',
    'services.cicd.description':
      'Automatiserade distributionspipelines som säkerställer snabb, pålitlig och konsekvent mjukvaruleverans.',
    'services.data.title': 'Datalösningar',
    'services.data.description':
      'Databasdesign, optimering och skapande av datapipelines för effektiv informationshantering.',
    'services.security.title': 'Säkerhetskonsultation',
    'services.security.description':
      'Omfattande säkerhetsrevisioner och implementation av robusta cybersäkerhetsåtgärder.',

    // AI Section
    'aiSection.badge': 'AI för er data',
    'aiSection.title': 'Skräddarsydda AI‑chatbots för ert företag',
    'aiSection.description':
      'Vi bygger säkra AI‑assistenter tränade på era dokument, kunskapsbaser och processer. Distribuera på er webbplats, i Slack/Teams eller i er produkt.',
    'aiSection.point1': 'Tränad på era PDF:er, dokument, wikis och ärendedata',
    'aiSection.point2': 'Träffsäkra svar med källhänvisningar och dokumenthämtning (RAG)',
    'aiSection.point3': 'Säker som standard: er data förblir privat',
    'aiSection.point4': 'Flera kanaler: webbwidget, Slack/Teams, intern portal',
    'aiSection.point5': 'Klara för regelefterlevnad: GDPR-anpassad, rollbaserad åtkomst',
    'aiSection.cta': 'Diskutera er AI‑assistent',

    // Pillars (home)
    'pillars.title': 'Vad Vi Gör',
    'pillars.description':
      'Fyra expertområden som tillsammans tar er organisation från idé till varaktig förändring.',
    'pillars.dt.title': 'Digital Transformation',
    'pillars.dt.description':
      'Förändringsledning och strategi som kopplar digitaliseringen till verksamhetsmål — så att ny teknik faktiskt skapar värde.',
    'pillars.management.title': 'Ledning & Ledarskap',
    'pillars.management.description':
      'Utveckla de ledarförmågor som avgör om den digitala resan lyckas — från strategiskt tänkande till datadrivna beslut.',
    'pillars.security.title': 'Säkerhet & Integritet',
    'pillars.security.description':
      'Praktiska verktyg och vägledning för att skydda konton och personuppgifter — intrångskontroller, GDPR‑begäran om radering och mer.',
    'pillars.software.title': 'Mjukvaruutveckling',
    'pillars.software.description':
      'Fullstack-utveckling, AI-integrationer och molnteknik — skalbar mjukvara skräddarsydd för er verksamhet.',
    'pillars.explore': 'Läs mer',

    // Digital Transformation page
    'dt.hero.badge': 'Erbjudande 2026',
    'dt.hero.titlePre': 'Förändringsledning och',
    'dt.hero.titleAccent': 'ledarskap',
    'dt.hero.titlePost': 'inom digital transformation.',
    'dt.hero.description':
      'Digital transformation handlar inte enbart om att införa ny teknik. Det handlar framför allt om att förändra arbetssätt, kultur, kompetenser och beteenden — för att skapa värde för verksamheten och dess invånare, kunder eller användare.',
    'dt.hero.ctaContact': 'Boka ett samtal',
    'dt.hero.ctaManagement': 'Fem viktiga ledarförmågor',
    'dt.hero.quote': '”Människor följer inte en teknik — de följer en meningsfull vision.”',
    'dt.hero.imageAlt': 'Ledarskap inom digital transformation',
    'dt.change.badge': 'Förändringsledning',
    'dt.change.title': 'Nya lösningar får värde först när människor faktiskt använder dem.',
    'dt.change.description':
      'Förändringsledning syftar till att säkerställa att människor förstår, accepterar och använder nya lösningar och arbetssätt. Det är bron mellan teknikinvesteringen och den verkliga effekten i verksamheten.',
    'dt.leadership.badge': 'Ledarskap',
    'dt.leadership.title': 'Från kontroll till riktning, engagemang och lärande.',
    'dt.leadership.description':
      'Dagens digitala ledarskap handlar mindre om kontroll och mer om att skapa riktning, engagemang och lärande. Ledaren är den som håller ihop teknik, människor och verksamhet över tid.',
    'dt.leadership.link': 'Utforska ledning & ledarskap',
    'dt.challenges.badge': 'Vanliga Utmaningar',
    'dt.challenges.title': 'Där ledarskapet gör skillnad.',
    'dt.challenges.description':
      'Samma mönster återkommer i organisation efter organisation. Här är vad ledarskapets roll är i praktiken.',
    'dt.challenges.1.challenge': 'Motstånd mot förändring',
    'dt.challenges.1.role': 'Skapa förståelse och delaktighet',
    'dt.challenges.2.challenge': 'Otydlig målbild',
    'dt.challenges.2.role': 'Kommunicera riktning och nytta',
    'dt.challenges.3.challenge': 'Bristande digital kompetens',
    'dt.challenges.3.role': 'Säkerställa lärande och stöd',
    'dt.challenges.4.challenge': 'Silotänkande',
    'dt.challenges.4.role': 'Främja samarbete över gränser',
    'dt.challenges.5.challenge': 'Förändringströtthet',
    'dt.challenges.5.role': 'Prioritera, fira framsteg, skapa energi',
    'dt.cta.badge': 'Låt Oss Prata',
    'dt.cta.title': 'Rusta era ledare för nästa steg i den digitala transformationen.',
    'dt.cta.description':
      'Vi arbetar tillsammans med ledningsgrupper, chefer och förändringsledare i offentlig sektor och näringsliv — med program, coachning och strategiskt stöd.',
    'dt.cta.button': 'Boka ett samtal',

    // Management page
    'management.hero.badge': 'Ledning & Ledarskap',
    'management.hero.title': 'Ledarskap som håller ihop teknik, människor och verksamhet.',
    'management.hero.description':
      'Digital transformation sker i komplexa miljöer där alla svar inte finns från början. Modernt ledarskap handlar om att skapa riktning, engagemang och lärande — och att hålla ihop teknik, människor och verksamhet över tid.',
    'management.comp.badge': 'Viktiga Ledarförmågor',
    'management.comp.title': 'Fem förmågor som avgör om den digitala resan lyckas.',
    'management.comp.1.title': 'Strategiskt tänkande',
    'management.comp.1.lead': 'Koppla digitaliseringen till verksamhetsmål och samhällsnytta.',
    'management.comp.1.body':
      'Ledaren behöver kunna översätta teknik till affärs- och samhällsvärde — och ständigt ställa frågan: Hur bidrar digitaliseringen till bättre resultat för verksamheten?',
    'management.comp.2.title': 'Leda i osäkerhet',
    'management.comp.2.lead': 'Våga experimentera, hantera risker, främja innovation.',
    'management.comp.2.body':
      'Digital transformation sker i komplexa miljöer där alla svar inte finns från början. Modiga ledare skapar rum för lärande och prövande.',
    'management.comp.3.title': 'Kommunikativt ledarskap',
    'management.comp.3.lead': 'Människor följer inte en teknik — de följer en meningsfull vision.',
    'management.comp.3.body':
      'Förklara varför förändringen är viktig, skapa engagemang och möt oro och motstånd med tydlighet och närvaro.',
    'management.comp.4.title': 'Tillitsbaserat ledarskap',
    'management.comp.4.lead': 'Från styrning och kontroll till förtroende och samverkan.',
    'management.comp.4.body':
      'Digital utveckling kräver tvärfunktionellt samarbete och decentraliserat beslutsfattande — byggt på ansvarstagande och tillit.',
    'management.comp.5.title': 'Datadrivet ledarskap',
    'management.comp.5.lead': 'Beslut baseras på data, analys och uppföljning.',
    'management.comp.5.body':
      'Ledaren behöver kunna tolka data, ställa rätt frågor och omsätta insikter till verksamhetsutveckling.',
    'management.reflection.badge': 'Reflektionsfråga',
    'management.reflection.quote':
      '”Hur bidrar digitaliseringen till bättre resultat för er verksamhet?”',
    'management.reflection.note':
      'En fråga varje ledningsgrupp bör kunna svara på — konkret, mätbart och meningsfullt.',
    'management.dtLink': 'Läs om digital transformation',

    // Software Development page
    'software.hero.badge': 'Mjukvaruutveckling',
    'software.hero.title1': 'Expertlösningar för Mjukvara',
    'software.hero.title2': 'för Moderna Företag',
    'software.hero.description':
      'Fullstack-utveckling, AI-integrationer och moln tekniska lösningar skräddarsydda för dina unika affärsbehov. Förvandla dina idéer till skalbar mjukvara.',

    // Expertise
    'expertise.title': 'Teknisk Expertis',
    'expertise.description':
      'Vi arbetar med banbrytande teknologier för att leverera robusta, skalbara lösningar.',
    'expertise.more': 'Och många fler teknologier baserat på dina projektbehov',

    // About
    'about.title': 'Varför Välja Cernea Consulting?',
    'about.description':
      'Vi är ett specialiserat mjukvaruutvecklingsföretag med djup expertis inom fullstack-utveckling, AI-integrationer och molnteknik. Vårt uppdrag är att förvandla dina affärsidéer till kraftfulla, skalbara mjukvarulösningar.',
    'about.benefit1': 'Agil utvecklingsmetodik',
    'about.benefit2': 'Transparent kommunikation',
    'about.benefit3': 'Skalbar arkitekturdesign',
    'about.benefit4': 'Support och underhåll efter lansering',
    'about.benefit5': 'Konkurrenskraftiga priser',
    'about.benefit6': 'Snabba leveranstider',
    'about.discuss': 'Låt Oss Diskutera Ditt Projekt',
    'about.ready.title': 'Redo Att Komma Igång?',
    'about.ready.description':
      'Kontakta oss idag för en kostnadsfri konsultation och låt oss förverkliga din vision.',
    'about.email': 'E-post',
    'about.responseTime': 'Svarstid',
    'about.responseValue': 'Inom 24 timmar',
    'about.consultation': 'Konsultation',
    'about.consultationValue': 'Kostnadsfri första utvärdering',

    // Contact Modal
    'contact.title': 'Kontakta Oss',
    'contact.description':
      'Berätta hur vi kan assistera er eller om ditt projekt så återkommer vi inom 24 timmar.',
    'contact.email': 'E-post',
    'contact.emailPlaceholder': 'din.epost@foretag.se',
    'contact.message': 'Projektdetaljer',
    'contact.messagePlaceholder':
      'Berätta hur vi kan hjälpa till eller om ditt projekt, tidslinje och krav...',
    'contact.cancel': 'Avbryt',
    'contact.send': 'Skicka Meddelande',
    'contact.sending': 'Skickar...',
    'contact.error': 'Fel',
    'contact.fillFields': 'Vänligen fyll i alla fält',
    'contact.success': 'Meddelande Skickat!',
    'contact.successMessage': 'Tack för ditt intresse. Victor kommer att återkomma till dig snart!',
    'contact.errorMessage': 'Misslyckades att skicka meddelande. Vänligen försök igen.',

    // Customers
    'customers.title': 'Företag och Partners',
    'customers.description':
      'Vi har förmånen att arbeta med innovativa företag inom olika branscher.',

    // Footer
    'footer.description':
      'Expert mjukvarukonsultation, AI-integration och moln tekniska lösningar skräddarsydda för dina affärsbehov.',
    'footer.services': 'Tjänster',
    'footer.contact': 'Kontakt',
    'footer.location': 'Sverige',
    'footer.global': 'Tillgänglig för globala projekt',
    'footer.copyright': '2025 Cernea Consulting AB. Alla rättigheter förbehålls.',

    // News Dialog
    'news.title': 'Jönköping Unleashed',
    'news.description':
      'En konferens för dig som bygger framtidens tech – med ansvar, insikt och nyfikenhet',
    'news.whatToExpect': 'Vad du kan förvänta dig:',
    'news.aiRegulations': 'AI-regler och utveckling av ansvarsfull AI',
    'news.webTech': 'Kraftfull webbteknik som distributionsplattform',
    'news.accessibility': 'Tillgänglighet i webbutveckling',
    'news.agile': 'Agil utveckling i stora organisationer',
    'news.networking': 'Networking och mingel med tech-proffs',
    'news.freeEvent': 'Gratis event!',
    'news.eventDescription':
      'En tech konferens med fokus på nutida processer, lagar, tekniker samt arbetssätt i den ständigt förändrande tech världen.',
    'news.getTickets': 'Skaffa biljetter',
    'news.maybeLater': 'Kanske senare',
    'news.imageAlt': 'Jönköping Unleashed Conference - Klicka för att besöka evenemangssidan',

    // Conference page
    'conference.techConference': 'Techkonferens',
    'conference.location': 'Jönköping, Sverige',
    'conference.pretitle': 'Bygg framtiden — tillsammans',
    'conference.formatLabel': 'Format',
    'conference.locationLabel': 'Plats',
    'conference.vibeLabel': 'Stämning',
    'conference.aiShort': 'AI',

    // Security page
    'security.title': 'Säkerhet & Integritet Online',
    'security.intro':
      'Praktiska tips för att skydda din integritet online och snabbt kontrollera om ett lösenord förekommit i dataintrång.',
    'security.tips.title1': 'Använd en lösenordshanterare',
    'security.tips.desc1':
      'Skapa unika, långa lösenord för varje webbplats och lagra dem i en betrodd hanterare.',
    'security.tips.title2': 'Aktivera tvåfaktorsautentisering (2FA)',
    'security.tips.desc2':
      'Lägg till ett extra steg vid inloggning så att konton är säkra även om ett lösenord läcker.',
    'security.tips.title3': 'Akta dig för phishing',
    'security.tips.desc3':
      'Kontrollera avsändaradresser och webbadresser; dela aldrig lösenord via e‑post eller chatt.',
    'security.checker.badge': 'Lösenordshälsa',
    'security.checker.title': 'Kontrollera om ditt lösenord är läckt',
    'security.checker.subtitle':
      'Ditt lösenord hashas lokalt med SHA‑1 och endast de första 5 tecknen av hashvärdet skickas till Have I Been Pwned (k‑anonymitet).',
    'security.checker.label': 'Lösenord att kontrollera',
    'security.checker.placeholder': 'Ange ett lösenord att testa (återanvänd inte detta lösenord)',
    'security.checker.show': 'Visa',
    'security.checker.hide': 'Dölj',
    'security.checker.checkButton': 'Kontrollera lösenord',
    'security.checker.loading': 'Kontrollerar…',
    'security.checker.result.safe': 'Goda nyheter — detta lösenord hittades inte i kända intrång.',
    'security.checker.result.pwned':
      'Varning — detta lösenord förekommer {count} gånger i kända intrång. Byt det överallt där det används.',
    'security.checker.result.error': 'Något gick fel. Försök igen senare.',
    'security.checker.errorEmpty': 'Ange ett lösenord.',
    'security.checker.privacyNote':
      'Vi skickar aldrig ditt faktiska lösenord. Endast en 5‑teckens prefix av dess SHA‑1‑hash delas med HIBP‑API:et.',
    'security.email.badge': 'E‑postläckor',
    'security.email.title': 'Kontrollera om din e‑post förekommer i intrång',
    'security.email.subtitle':
      'Använd Have I Been Pwned för att se om din e‑postadress har exponerats i kända dataintrång.',
    'security.email.description':
      'Kontrollen görs på Have I Been Pwneds webbplats. Återanvänd aldrig lösenord och aktivera 2FA där det finns.',
    'security.email.cta': 'Kontrollera din e‑post på HIBP',
    'security.notify.badge': 'E‑postnotiser',
    'security.notify.title': 'Få notiser om din e‑post har läckt',
    'security.notify.subtitle':
      'Have I Been Pwned kan meddela dig om din e‑postadress dyker upp i ett nytt intrång.',
    'security.notify.description':
      'Registrera din e‑post för att få varningar. Använd starka, unika lösenord och aktivera 2FA för att minska riskerna.',
    'security.notify.cta': 'Anmäl dig för notiser på HIBP',
    'security.top.contactPrompt': 'Har du frågor eller behöver hjälp?',
    'security.top.contactLink': 'Kontakta oss',

    // GDPR Erasure
    'security.gdpr.badge': 'GDPR & borttagning',
    'security.gdpr.title': 'Ta bort din publika information från söktjänster och rättsdatabaser',
    'security.gdpr.subtitle':
      'Enligt GDPR (Art. 17) kan du begära radering av personuppgifter. Välj leverantörer och skicka en begäran om borttagning. ',
    'security.gdpr.searchLabel': 'Sök leverantörer',
    'security.gdpr.searchPlaceholder': 'Sök leverantörer efter namn, land eller kategori…',
    'security.gdpr.selectAll': 'Markera alla',
    'security.gdpr.clear': 'Rensa',
    'security.gdpr.selectedCount': '{count} valda',
    'security.gdpr.cta': 'Kontakta valda leverantörer',
    'security.gdpr.disclaimer':
      'Detta verktyg hjälper dig att formulera e‑post till leverantörers privacy-team. Begäran skickas för din räkning med din e‑post som Reply‑To så att de kan svara direkt.',
    'security.gdpr.googleRemoval.title': 'Begär borttagning från Google Sök',
    'security.gdpr.googleRemoval.description':
      'Om känsliga eller inaktuella resultat om dig visas i Google Sök kan du be Google att ta bort personligt innehåll och blockera liknande sökningar framöver.',
    'security.gdpr.googleRemoval.cta': 'Öppna Googles formulär',

    // GDPR Modal
    'security.gdpr.modal.title': 'Skicka GDPR‑begäran om radering',
    'security.gdpr.modal.description':
      'Lämna tillräcklig information för att verifiera din identitet och hitta dina uppgifter.',
    'security.gdpr.modal.providerCount': 'Du kommer kontakta {count} leverantör(er).',
    'security.gdpr.modal.email': 'Din e‑post',
    'security.gdpr.modal.fullName': 'Fullständigt namn',
    'security.gdpr.modal.personalNr': 'Personnummer',
    'security.gdpr.modal.personalNrPlaceholder': 'ÅÅÅÅMMDD-XXXX',
    'security.gdpr.modal.identifiers': 'Identifierare (frivilligt)',
    'security.gdpr.modal.identifiersPlaceholder': 'Andra e‑postadresser, alias, telefonnummer…',
    'security.gdpr.modal.country': 'Land/Region (frivilligt)',
    'security.gdpr.modal.links': 'Profillänkar / URL:er (frivilligt)',
    'security.gdpr.modal.linksPlaceholder':
      'En per rad, t.ex. profilsidor eller sökresultat‑URL:er',
    'security.gdpr.modal.message': 'Ytterligare information (frivilligt)',
    'security.gdpr.modal.messagePlaceholder':
      'Hej!\n\n\nMitt personnummer är:\n\nJag vill härmed radera mina uppgifter om mig.\n\nJag begär att alla personuppgifter som är kopplade till mig tas bort från era databaser och webbplatser. Detta innebär att ingen person ska kunna se information om mig, inklusive rättsliga dokument och andra uppgifter kopplade till mitt namn och personnummer, i er tjänst. Jag vill även att ni, så långt det är möjligt, uppgiftsminimerar och raderar de uppgifter ni håller om mig internt.\n\nNi kan hitta information om era skyldigheter på Integritetsskyddsmyndighetens webbplats (https://www.imy.se).\n\nJag ser fram emot bekräftelse på min begäran om radering snarast möjligt. Ni får gärna kontakta mig om ni har några frågor.\n\n\nMed vänliga hälsningar, ',
    'security.gdpr.modal.help':
      'Inkludera ditt fullständiga namn, din e‑post och ditt personnummer. Du kan lägga till andra identifierare vid behov.',
    'security.gdpr.modal.previewHeading': 'Förhandsgranskning av e‑post',
    'security.gdpr.modal.cancel': 'Avbryt',
    'security.gdpr.modal.send': 'Öppna e‑postklient',
    'security.gdpr.modal.sending': 'Skickar…',
    'security.gdpr.toast.successTitle': 'Begäran skickade',
    'security.gdpr.toast.partialTitle': 'Vissa begäran misslyckades',
    'security.gdpr.toast.summary': 'Begäran skickade: {success} lyckades, {failed} misslyckades',
    'security.gdpr.toast.missingConfig': 'E‑postkonfiguration saknas. Kontakta support.',

    // GDPR E‑postinnehåll (förifylld e‑post)
    'security.gdpr.email.subject': 'Begäran om radering av personuppgifter (GDPR)',
    'security.gdpr.email.greetingGeneric': 'Hej integritetsteam,',
    'security.gdpr.email.greetingProvider': 'Hej {provider} integritetsteam,',
    'security.gdpr.email.requestLine':
      'Jag begär radering av mina personuppgifter enligt GDPR artikel 17.',
    'security.gdpr.email.field.fullName': 'Fullständigt namn',
    'security.gdpr.email.field.replyTo': 'E‑post (svar till)',
    'security.gdpr.email.field.identifiers': 'Identifierare',
    'security.gdpr.email.field.country': 'Land/Region',
    'security.gdpr.email.linksHeading': 'Profillänkar / URL:er:',
    'security.gdpr.email.closingConfirm':
      'Vänligen bekräfta mottagandet och meddela när raderingen är genomförd.',
    'security.gdpr.email.thanks': 'Tack.',

    // Common
    'common.home': 'Hem',
    'common.back': 'Tillbaka',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = globalThis.window?.localStorage.getItem('language') ?? null;

      if (saved === 'en' || saved === 'sv') return saved;
      return 'sv';
    } catch {
      return 'sv';
    }
  });

  useEffect(() => {
    try {
      if (globalThis.window) {
        globalThis.window.localStorage.setItem('language', language);
      }
    } catch {
      // ignore storage errors
    }
  }, [language]);

  const t = useCallback(
    (key: string): string => {
      return translations[language][key as keyof (typeof translations)[typeof language]] || key;
    },
    [language],
  );

  const contextValue = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
